/**
 * RepairComposer — message composer for Repair Detail (RA-COMPOSE-AGENT,
 * RA-COMPOSE-PARTY, RA-COMPOSE-PARTIES, RA-COMPOSE-VISIBILITY, BR-SUSPEND-CAUSE).
 *
 * Iteration 12 (UX restructure): recipient selection is now CONTROLLED by the parent
 * (RepairDetailPage's per-party thread list, RA-CONVERSATION-THREADS) via
 * `selectedThreadKey`/`parties` — this component no longer owns a mode radio or fetches
 * its own party list. It renders only the message textarea + send action for whichever
 * thread is currently selected:
 *   - `selectedThreadKey === 'agent'` — "Agent notes" thread: a note only the agent sees
 *     (case.comment_added, landlord-authored); never visible to the tenant/vendor. Uses
 *     POST .../repairs/:id/messages.
 *   - otherwise — a direct message to the tenant or an assigned/contacted vendor
 *     identified by `selectedThreadKey` (case.message_sent). This is a landlord takeover
 *     of the conversation and pauses the agent on this case until handed back
 *     (BR-SUSPEND-CAUSE), same as the leasing side's direct-reply takeover —
 *     CaseEscalationSection's paused banner (rendered by the caller) is what surfaces
 *     that pause. Uses POST .../repairs/:id/party-messages.
 *
 * `partyOptionValue`/`partyOptionLabel` are exported for RepairDetailPage's thread list to
 * reuse (both need the exact same `${partyType}:${id}` keying and disabled-reason label
 * text) — kept here rather than duplicated or split into a new shared util file, since
 * this component already owned them and RepairDetailPage already imports this module.
 *
 * Types are derived from the boundary contract in `@resida/api-types/repair` via
 * `apps/landlord/src/api/repairs.api.ts` — the same Zod schemas apps/api registers on
 * these routes.
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  postRepairMessage,
  postRepairPartyMessage,
  type RepairParty,
} from '../../api/repairs.api';

interface RepairComposerProps {
  repairId: string;
  /** The externally-selected thread: 'agent' (Agent notes) or a specific party, keyed the
   *  same way RepairParty's identity works (`${partyType}:${id}`, see partyOptionValue).
   *  Replaces the old internally-owned `mode`/`selectedParty` state and the mode radio. */
  selectedThreadKey: string;
  /** The full party list — lifted to RepairDetailPage so it's fetched once and shared
   *  between the thread list and this composer, instead of this component fetching it
   *  independently. */
  parties: RepairParty[] | null;
  partiesError: string | null;
  /** True when the case has an open escalation (repair.agentState ===
   *  'escalation_pending') — surfaces a hint pointing the landlord at the escalation
   *  card above instead of using this composer's agent thread to answer it. */
  escalationOpen: boolean;
  /** Called after a successful send — the caller reloads repair + timeline (and the
   *  escalation/suspension section), since a party send can pause the agent. */
  onSent: () => void;
}

export function partyOptionValue(party: RepairParty): string {
  return `${party.partyType}:${party.id}`;
}

export function partyOptionLabel(party: RepairParty): string {
  const kind = party.partyType === 'tenant' ? 'Tenant' : 'Vendor';
  let label = `${party.displayName} — ${kind}`;
  if (party.assigned) label += ' (assigned)';
  if (party.contacted) label += ' (contacted)';
  if (!party.contactable && party.notContactableReason) {
    label += ` — ${party.notContactableReason}`;
  }
  return label;
}

export function RepairComposer({
  repairId,
  selectedThreadKey,
  parties,
  partiesError,
  escalationOpen,
  onSent,
}: RepairComposerProps) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcomeMsg, setOutcomeMsg] = useState<string | null>(null);
  // 'blocked' isn't a success (nothing was sent) — styled distinctly from sent/scheduled.
  const [outcomeKind, setOutcomeKind] = useState<'sent' | 'scheduled' | 'blocked' | null>(null);

  const isAgentThread = selectedThreadKey === 'agent';
  const selectedParty = isAgentThread
    ? undefined
    : parties?.find((p) => partyOptionValue(p) === selectedThreadKey);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    setError(null);
    setOutcomeMsg(null);
    setOutcomeKind(null);
    try {
      if (isAgentThread) {
        await postRepairMessage(repairId, { content: content.trim() });
        setOutcomeMsg('Sent to the agent.');
        setOutcomeKind('sent');
      } else {
        if (!selectedParty) {
          setError('Choose who to send this to.');
          return;
        }
        const result = await postRepairPartyMessage(repairId, {
          recipientType: selectedParty.partyType,
          vendorId: selectedParty.partyType === 'vendor' ? selectedParty.id : undefined,
          content: content.trim(),
        });
        setOutcomeMsg(
          result.outcome === 'sent'
            ? `Sent to ${selectedParty.displayName}.`
            : result.outcome === 'scheduled'
              ? 'Scheduled — it will send after quiet hours.'
              : 'Not sent — blocked by a delivery rule.'
        );
        setOutcomeKind(result.outcome);
      }
      setContent('');
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const sendDisabled =
    sending || !content.trim() || (!isAgentThread && (!selectedParty || !selectedParty.contactable));

  // Docked footer of the Conversation card (no card/heading of its own) — the parent renders
  // it inside the card, under the thread panel, so "who am I sending to" reads off the same
  // surface as the thread the landlord is looking at.
  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      data-testid="repair-composer"
      className="flex flex-col gap-2 border-t border-border px-4 py-3 md:px-5"
    >
      <div className="flex flex-col gap-0.5">
        <p className="m-0 text-[11px] text-muted-foreground">
          Sending to:{' '}
          <strong className="font-bold text-foreground">
            {isAgentThread ? 'Agent notes' : (selectedParty?.displayName ?? 'Unknown')}
          </strong>
        </p>
        {isAgentThread ? (
          <p className="m-0 text-[11px] text-muted-foreground">
            A note only the agent sees — it will act on it.
          </p>
        ) : (
          <>
            {partiesError && (
              <p role="alert" className="m-0 text-xs text-destructive">{partiesError}</p>
            )}
            <p className="m-0 text-[11px] text-muted-foreground">
              Sending to a party pauses the agent on this case until you hand it back.
            </p>
          </>
        )}
      </div>

      {isAgentThread && escalationOpen && (
        <p
          data-testid="repair-composer-escalation-hint"
          className="m-0 text-xs text-muted-foreground rounded-md border border-border bg-muted/40 p-2"
        >
          The agent is waiting on your answer — reply in the escalation card above.
        </p>
      )}

      <div className="flex items-end gap-2">
        <label htmlFor="repair-composer-input" className="sr-only">
          Message
        </label>
        <Textarea
          id="repair-composer-input"
          data-testid="repair-composer-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={4000}
          rows={1}
          placeholder={
            isAgentThread
              ? 'Send a note to the agent…'
              : `Message ${selectedParty?.partyType === 'vendor' ? 'the vendor' : 'the tenant'}…`
          }
          disabled={sending}
          className="min-h-[44px] flex-1 resize-y py-2.5"
        />
        <Button
          type="submit"
          data-testid="repair-composer-send-button"
          className="min-h-[44px] px-5 font-bold"
          disabled={sendDisabled}
          aria-disabled={sendDisabled}
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </div>

      {error && (
        <p role="alert" data-testid="repair-composer-error" className="m-0 text-sm text-destructive">
          {error}
        </p>
      )}

      {outcomeMsg && (
        <p
          role="status"
          aria-live="polite"
          data-testid="repair-composer-outcome"
          className={`m-0 text-sm ${outcomeKind === 'blocked' ? 'text-destructive' : outcomeKind === 'scheduled' ? 'text-muted-foreground' : 'text-success'}`}
        >
          {outcomeMsg}
        </p>
      )}
    </form>
  );
}

export default RepairComposer;
